import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ChevronRight, Loader2, RotateCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { PatientEncounter } from '@/api/patientsApi'
import { usePatientEncounters } from '@/hooks/usePatientEncounters'
import { PatientVisitsTable } from '@/components/patient/PatientVisitsTable'
import type { Procedure, ProcedureStatus } from '@/types/cgt'
import { cn } from '@/lib/utils'

interface PatientHistoryPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The patient's other runs, each with its `progress` (`usePatientRuns`). */
  procedures: Procedure[]
  /** No rows yet because the list is still on its way. */
  loading?: boolean
  /** The list failed, and there are no rows to show instead. */
  error?: unknown
  onRetry?: () => void
  patientMrn: string
}

const STATUS_PILL: Record<ProcedureStatus, { label: string; className: string }> = {
  in_progress: { label: 'In progress', className: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', className: 'bg-rose-100 text-rose-700' },
}

// Per-user column layout. Transplant is locked (it identifies the row) and so
// is the chevron that opens it. See src/components/table/index.ts.
const PATIENT_HISTORY_TABLE_ID = 'patient-history-procedures'

const PATIENT_HISTORY_COLUMNS: TableColumnDef[] = [
  { id: 'transplant', header: 'Transplant', locked: true, sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'progress', header: 'Progress', sortable: true },
  { id: 'started', header: 'Started', sortable: true },
  { id: 'createdBy', header: 'Created by', sortable: true },
  { id: 'open', header: 'Open', locked: true },
]

// Column sorting (z8nrz7e4h6). Progress sorts by the share done, as its bar
// shows; a journey with no steps has no share and sorts last.
const PATIENT_HISTORY_SORT: SortAccessors<Procedure> = {
  transplant: p => p.procedureTemplate.name,
  status: p => STATUS_PILL[p.status]?.label,
  progress: p => (p.progress && p.progress.total > 0 ? p.progress.completed / p.progress.total : null),
  started: p => p.startedAt,
  createdBy: p => p.createdBy,
}

type HistoryTab = 'transplants' | 'visits'

/**
 * The two histories a patient has, side by side and never merged: the Title21
 * transplants we run, and the Epic admissions ADT told us about. They answer
 * different questions and are keyed differently — a procedure by its template, a
 * visit by PV1-19.1 — so mixing them into one table would force a row shape that
 * lies about both.
 *
 * ## Layout
 *
 * The dialog is height-capped and the TABLE scrolls, not the dialog. Without
 * that, a patient with nineteen procedures grew the content past the viewport and
 * — because the content is centred with `translate-y-[-50%]` — it was clipped at
 * the TOP as well as the bottom, taking the header, the title and the close
 * button off screen with no way to scroll them back. Hence
 * `max-h-[85vh] + flex-col + overflow-hidden` here, `min-h-0 flex-1 overflow-auto`
 * on the scroller (a flex child will not shrink below its content without
 * `min-h-0`), and a sticky `thead` so the column names survive the scroll.
 */
export function PatientHistoryPanel({
  open,
  onOpenChange,
  procedures,
  loading = false,
  error,
  onRetry,
  patientMrn,
}: PatientHistoryPanelProps) {
  const [tab, setTab] = useState<HistoryTab>('transplants')
  const encounters = usePatientEncounters(patientMrn, open)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[85vh] w-[min(64rem,95vw)] max-w-none flex-col gap-4 overflow-hidden'>
        <DialogHeader className='shrink-0 pr-8'>
          <DialogTitle>Patient History</DialogTitle>
          <DialogDescription>
            {tab === 'transplants'
              ? 'All other transplants for this patient.'
              : 'Hospital visits for this patient, as Epic reported them.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={value => setTab(value as HistoryTab)}
          className='flex min-h-0 flex-1 flex-col'
        >
          <TabsList className='shrink-0 self-start'>
            <TabsTrigger value='transplants'>Transplants</TabsTrigger>
            <TabsTrigger value='visits'>
              Patient Visit History
              {encounters.data && encounters.data.length > 0 ? (
                <span className='ml-1.5 text-xs opacity-70'>({encounters.data.length})</span>
              ) : null}
            </TabsTrigger>
          </TabsList>

          {/* Inactive panels unmount, which is what keeps `dialog.locator('table')`
              unambiguous for the e2e suite — and means neither table's column
              menu can be reached while its tab is closed. */}
          <TabsContent value='transplants' className='mt-4 flex min-h-0 flex-1 flex-col'>
            <ProceduresTab
              procedures={procedures}
              loading={loading}
              error={error}
              onRetry={onRetry}
              patientMrn={patientMrn}
              onNavigate={() => onOpenChange(false)}
            />
          </TabsContent>

          <TabsContent value='visits' className='mt-4 flex min-h-0 flex-1 flex-col'>
            <VisitsTab
              rows={encounters.data}
              loading={encounters.isPending}
              error={encounters.error}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Sticky header cells.
 *
 * On the `<th>`, not the `<thead>`: a sticky `thead` is only as tall as its cell
 * box, so a row scrolling underneath bled through above the column names. The
 * background and the bottom border therefore live on the cells, and `pt-3`
 * matches the body rows' `py-3` so the band is tall enough to cover one.
 */
const STICKY_TH = 'sticky top-0 z-10 border-b bg-background pb-2 pt-3'

/** Shared frame for a tab: one scroll container, one sticky header. */
function TableScroller({ children }: { children: React.ReactNode }) {
  return (
    <div className='overflow-x-persistent min-h-0 flex-1 overflow-y-auto'>
      <table className='w-full text-sm'>{children}</table>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className='flex flex-1 items-center justify-center py-8'>{children}</div>
}

function Spinner() {
  return (
    <Centered>
      <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
    </Centered>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Centered>
      <p className='text-center text-sm text-muted-foreground'>{children}</p>
    </Centered>
  )
}

// ─── Transplants ───────────────────────────────────────────────────────────────

/**
 * The patient's other transplants, as the list gives them: each row carries
 * its own step progress, counted for that run alone (z8nrz7fzk7). This used to
 * fetch a journey per transplant, five at a time, and showed nothing until the
 * last one answered, so one slow request held the whole table.
 */
function ProceduresTab({
  procedures,
  loading,
  error,
  onRetry,
  patientMrn,
  onNavigate,
}: {
  procedures: Procedure[]
  loading: boolean
  error: unknown
  onRetry?: () => void
  patientMrn: string
  onNavigate: () => void
}) {
  const cols = useTableColumns(PATIENT_HISTORY_TABLE_ID, PATIENT_HISTORY_COLUMNS)
  const navigate = useNavigate()
  const rows = useSortedRows(procedures, cols.sort, PATIENT_HISTORY_SORT)

  if (procedures.length === 0 && error) {
    return (
      <Centered>
        <div className='flex max-w-sm flex-col items-center gap-2 text-center'>
          <AlertCircle className='h-5 w-5 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>
            Could not load this patient's transplants: {error instanceof Error ? error.message : 'unknown error'}
          </p>
          {onRetry && (
            <button
              type='button'
              onClick={onRetry}
              className='flex items-center gap-1 text-sm font-medium text-primary hover:underline'
            >
              <RotateCw className='h-3 w-3' /> Try again
            </button>
          )}
        </div>
      </Centered>
    )
  }
  if (procedures.length === 0 && loading) return <Spinner />
  if (procedures.length === 0) return <Empty>No other transplants found for this patient.</Empty>

  return (
    <TableScroller>
      <thead>
        <tr className='text-left text-xs font-medium text-muted-foreground'>
          {cols.visible.map(({ def }) => (
            <ResizableTh
              key={def.id}
              columnId={def.id}
              columns={cols}
              label={def.header}
              menuLabel='Transplant columns'
              className={cn(STICKY_TH, def.id === 'open' ? 'w-8' : 'pr-4')}
              resizable={def.id !== 'open'}
            >
              {def.id === 'open' ? null : def.header}
            </ResizableTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(p => {
          const pill = STATUS_PILL[p.status]
          const pct = p.progress && p.progress.total > 0 ? (p.progress.completed / p.progress.total) * 100 : 0
          const goTo = () => {
            onNavigate()
            navigate(`/patients/${encodeURIComponent(patientMrn)}/procedures/${p.id}`)
          }
          return (
            <tr
              key={p.id}
              role='button'
              tabIndex={0}
              className='cursor-pointer border-b last:border-0 hover:bg-accent/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
              onClick={goTo}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  goTo()
                }
              }}
            >
              <ColumnCells
                columns={cols}
                cells={{
                  transplant: (
                    <td className='py-3 pr-4 font-medium'>{p.procedureTemplate.name}</td>
                  ),
                  status: (
                    <td className='py-3 pr-4'>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          pill.className,
                        )}
                      >
                        {pill.label}
                      </span>
                    </td>
                  ),
                  progress: (
                    <td className='py-3 pr-4'>
                      <div className='flex items-center gap-2'>
                        <div className='h-1.5 w-16 overflow-hidden rounded-full bg-muted'>
                          <div
                            className='h-full rounded-full bg-primary transition-all'
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className='text-xs text-muted-foreground'>
                          {/* Absent only from a backend older than progress=run. */}
                          {p.progress ? `${p.progress.completed}/${p.progress.total}` : '—'}
                        </span>
                      </div>
                    </td>
                  ),
                  started: (
                    <td className='py-3 pr-4 text-muted-foreground'>
                      {new Date(p.startedAt).toLocaleDateString()}
                    </td>
                  ),
                  createdBy: (
                    <td className='py-3 pr-4 text-muted-foreground'>{p.createdBy}</td>
                  ),
                  open: (
                    <td className='py-3'>
                      <ChevronRight className='h-4 w-4 text-muted-foreground' />
                    </td>
                  ),
                }}
              />
            </tr>
          )
        })}
      </tbody>
    </TableScroller>
  )
}

// ─── Epic visits ───────────────────────────────────────────────────────────────

/**
 * Epic visit history.
 *
 * The table itself is `PatientVisitsTable`, shared with the Patient Visit
 * History dialog on the patient detail page (z8nrz7e0rb) so the two cannot
 * disagree. What stays here is only what belongs to THIS dialog: `sticky`,
 * because the table is the only thing allowed to scroll inside a height-capped
 * dialog.
 */
function VisitsTab({
  rows,
  loading,
  error,
}: {
  rows: PatientEncounter[] | undefined
  loading: boolean
  error: unknown
}) {
  return <PatientVisitsTable rows={rows} loading={loading} error={error} sticky />
}
